import { useState } from 'react';
import { Button, Alert, FormGroup, Row, Col } from 'reactstrap';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js';
import ENV_VARS from '../../utils/envVars';
import { STRIPE_ELEMENT_STYLE_PROPS } from '../../constants';

const stripePromise = ENV_VARS.STRIPE_PUBLISHABLE_KEY && ENV_VARS.STRIPE_PUBLISHABLE_KEY.startsWith('pk_')
  ? loadStripe(ENV_VARS.STRIPE_PUBLISHABLE_KEY)
  : null;

function UpdateBillingForm({ onSuccess, onBack }) {
  const stripe = useStripe();
  const elements = useElements();
  const [nameOnCard, setNameOnCard] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    if (!nameOnCard.trim()) {
      setError('Name on card is required.');
      return;
    }
    setLoading(true);
    setError(null);

    const { paymentMethod, error: pmError } = await stripe.createPaymentMethod({
      type: 'card',
      card: elements.getElement(CardNumberElement),
    });

    if (pmError) {
      setError(pmError.message);
      setLoading(false);
      return;
    }

    onSuccess(paymentMethod.id);
    setLoading(false);
  };

  return (
    <form onSubmit={handleSubmit}>
      <FormGroup className="stacked">
        <label htmlFor="nameOnCard">Name on Card</label>
        <input
          id="nameOnCard"
          className="form-control"
          type="text"
          value={nameOnCard}
          onChange={(e) => setNameOnCard(e.target.value)}
          placeholder="Full name"
        />
      </FormGroup>
      <FormGroup className="stacked">
        <label>Card Number</label>
        <div className="stripe-element-container">
          <CardNumberElement options={{ ...STRIPE_ELEMENT_STYLE_PROPS, showIcon: true }} />
        </div>
      </FormGroup>
      <Row>
        <Col xs={8}>
          <FormGroup className="stacked">
            <label>Expiration Date</label>
            <div className="stripe-element-container">
              <CardExpiryElement options={STRIPE_ELEMENT_STYLE_PROPS} />
            </div>
          </FormGroup>
        </Col>
        <Col>
          <FormGroup className="stacked">
            <label>CVC</label>
            <div className="stripe-element-container">
              <CardCvcElement options={STRIPE_ELEMENT_STYLE_PROPS} />
            </div>
          </FormGroup>
        </Col>
      </Row>
      {error && <Alert color="danger">{error}</Alert>}
      <Button color="primary" size="lg" block type="submit" disabled={!stripe || loading}>
        {loading ? 'Updating...' : 'Update Billing Information'}
      </Button>
      <Button outline color="primary" size="lg" block className="mt-2" type="button" onClick={onBack}>
        Cancel
      </Button>
    </form>
  );
}

export default function UpdateBilling({ onSuccess, onBack, error }) {
  return (
    <div className="manage-update-billing">
      <button
        type="button"
        className="manage-sub-back-link mb-3"
        onClick={onBack}
        aria-label="Back"
      >
        <em className="fas fa-arrow-left" aria-hidden />
      </button>
      <h3 className="mb-1">Update Billing Information</h3>
      <p className="text-muted small mb-4">
        We use Stripe to process and manage all payments. All information is secure and confidential.
      </p>
      {error && <Alert color="danger">{error}</Alert>}
      <Elements stripe={stripePromise}>
        <UpdateBillingForm onSuccess={onSuccess} onBack={onBack} />
      </Elements>
    </div>
  );
}
